import Vue from "vue";
import VueRouter, { RouterOptions } from "vue-router";
import posthog from 'posthog-js';
import store, { storeModules, EDIT_STORE_MODULES } from "../store";

import { Message } from 'element-ui';
import EtcRoutes from "./etc-routes";
import AppRoutes from './app-routes';
import { resolveExpectedNavigationFailures } from "./navigation-failure";
import EventBus from "../utils/event-bus";
import { EVENT_BUS, ROUTER } from "../common/constants";
import { isAgendaWebviewLocation } from "../utils/agenda-webview-helper";
import { captureAppError } from "../utils/error-reporter";
import ModuleType from "../common/module-constant";
import i18n from "../i18n";

import KanbanWebsite from "../containers/kanban-website/KanbanWebsiteComponent.vue";
import EmailWebsite from "../containers/email-website/EmailWebsiteComponent.vue";
import Dashboard from "../containers/dashboard";
import MarketingDashboard from "../containers/dashboard/marketing-analytics/MarketingDashboardComponent.vue";
import AssistantArtifact from "../containers/assistant-artifact/AssistantArtifactComponent.vue";
import EnquiryCentre from "../containers/enquiry-centre/EnquiryCentreComponent.vue";

Vue.use(VueRouter);

// Define kanban routes
const kanbanWebsiteRoute = 
    {
        path: "/kanban-website",
        name: ROUTER.KANBAN,
        component: KanbanWebsite,
        meta: {
            title_key: "KANBAN_BETA",
            icon_name: "kanban",
            standalone: true,
            moduleType: ModuleType.Kanban
        },
        props: {},
        beforeEnter: (to, from, next) => {
            const isKanbanFlagEnabled = posthog.isFeatureEnabled('kanban-board')
            if (isKanbanFlagEnabled) {
              next()
            } else {
              next(false)
            }
        }
    }

const emailWebsiteRoute = 
    {
        path: "/emails-website",
        name: ROUTER.EMAIL_WEBSITE,
        component: EmailWebsite,
        meta: {
            keepAlive: true,
            title_key: "EMAILS_BETA",
            icon_name: "emails",
            standalone: true,
            moduleType: ModuleType.EmailWebsite
        },
        props: {},
        beforeEnter: (to, from, next) => {
            const isEmailFlagEnabled = posthog.isFeatureEnabled('automation-editor')
            if (isEmailFlagEnabled) {
              next()
            } else {
              next(false)
            }
        }
    }

const enquiryCentreRoute =
    {
        path: "/enquiry-centre",
        name: ROUTER.ENQUIRY_CENTRE,
        component: EnquiryCentre,
        meta: {
            title_key: "ENQUIRY_CENTRE",
            icon_name: "enquiry-task",
            standalone: true,
            moduleType: ModuleType.EnquiryCentre
        },
        props: {},
        beforeEnter: (to, from, next) => {
            const isEnquiryCentreFlagEnabled = posthog.isFeatureEnabled('enquiry-centre')
            if (isEnquiryCentreFlagEnabled) {
              next()
            } else {
              next(false)
            }
        }
    }

const dashboardRoute =
    {
        path: "/dashboard",
        name: ROUTER.DASHBOARD,
        component: Dashboard,
        meta: {
            title_key: "DASHBOARD",
            icon_name: "dashboard",
            standalone: true,
            moduleType: ModuleType.Dashboard
        },
        props: {},
        beforeEnter: (to, from, next) => {
            const isDashboardFlagEnabled = posthog.isFeatureEnabled('dashboard-visible-flag')
            if (isDashboardFlagEnabled) {
              next()
            } else {
              next(false)
            }
        }
    }

const marketingDashboardRoute =
    {
        path: "/marketing-dashboard",
        name: ROUTER.MARKETING_DASHBOARD,
        component: MarketingDashboard,
        meta: {
            title_key: "MARKETING_DASHBOARD_TITLE",
            icon_name: "marketing",
            standalone: true,
            moduleType: ModuleType.Dashboard
        },
        props: {},
        beforeEnter: (to, from, next) => {
            if (posthog.isFeatureEnabled('marketing-dashboard')) {
                next();
            } else {
                next(false);
            }
        }
    }

const assistantArtifactRoute = {
    path: "/assistant-artifact/:id",
    name: ROUTER.ASSISTANT_ARTIFACT,
    component: AssistantArtifact,
    meta: {
        title_key: "ASSISTANT_ARTIFACT",
        standalone: true,
        moduleType: ModuleType.AssistantArtifact,
    },
    props: {},
    beforeEnter: (to, from, next) => {
        const isAssistantFlagEnabled = posthog.isFeatureEnabled('ai-assistant')
        if (isAssistantFlagEnabled) {
          next()
        } else {
          next(false)
        }
    }
};

// Build base routes (without kanban initially)
let routes = [
    ...AppRoutes,
    ...EtcRoutes,
];

let config: RouterOptions = {
    routes: routes,
    linkActiveClass: "active"
};

const historyModeEl = document.querySelector("meta[name='History-Mode']");

const isHistoryMode = !!historyModeEl || location.port == '82';
if (isHistoryMode) {
    config.mode = "history";
    config.base = "/";
}

// The embedded agenda calendar URL must match the router mode (path form in
// history mode, hash form in hash mode); the wrong form resolves to "/" and
// falls into the /agenda redirect. Normalize instead of showing the wrong page.
const agendaWebviewInPath = location.pathname.indexOf("/agenda-webview") >= 0;
const agendaWebviewInHash = location.hash.indexOf("/agenda-webview") >= 0;
if (isHistoryMode && agendaWebviewInHash && !agendaWebviewInPath) {
    location.replace(location.hash.substring(1));
} else if (!isHistoryMode && agendaWebviewInPath && !agendaWebviewInHash) {
    location.replace(`/#${location.pathname}${location.search}`);
}

const router: VueRouter = new VueRouter(config);
const commonModule = `${storeModules.Common}`;

// Flag to track if kanban routes have been added
let kanbanRoutesAdded = false;
let emailWebsiteRoutesAdded = false;
let dashboardRoutesAdded = false;
let marketingDashboardRoutesAdded = false;
let assistantArtifactRoutesAdded = false;
let enquiryCentreRoutesAdded = false;

// Function to add kanban routes dynamically
function addKanbanRoutes() {
    if (!kanbanRoutesAdded && posthog.isFeatureEnabled('kanban-board')) {
        console.log('Adding kanban routes dynamically');
        router.addRoute(kanbanWebsiteRoute as any);
        kanbanRoutesAdded = true;
        return true;
    }
    return false;
}

function addDashboardRoutes() {
    if (!dashboardRoutesAdded && posthog.isFeatureEnabled('dashboard-visible-flag')) {
        console.log('Adding dashboard routes dynamically');
        router.addRoute(dashboardRoute as any);
        dashboardRoutesAdded = true;
        return true;
    }
    return false;
}

function addMarketingDashboardRoutes() {
    if (!marketingDashboardRoutesAdded && posthog.isFeatureEnabled('marketing-dashboard')) {
        console.log('Adding marketing dashboard routes dynamically');
        router.addRoute(marketingDashboardRoute as any);
        marketingDashboardRoutesAdded = true;
        return true;
    }
    return false;
}

function addEmailWebsiteRoutes() {
    if (!emailWebsiteRoutesAdded && posthog.isFeatureEnabled('automation-editor')) {
        console.log('Adding email website routes dynamically');
        router.addRoute(emailWebsiteRoute as any);
        emailWebsiteRoutesAdded = true;
        return true;
    }
    return false;
}

export function addAssistantArtifactRoutes() {
    if (!assistantArtifactRoutesAdded && posthog.isFeatureEnabled('ai-assistant')) {
        console.log('Adding assistant artifact routes dynamically');
        router.addRoute(assistantArtifactRoute as any);
        assistantArtifactRoutesAdded = true;
        return true;
    }
    return false;
}

function addEnquiryCentreRoutes() {
    if (!enquiryCentreRoutesAdded && posthog.isFeatureEnabled('enquiry-centre')) {
        console.log('Adding enquiry centre routes dynamically');
        router.addRoute(enquiryCentreRoute as any);
        enquiryCentreRoutesAdded = true;
        return true;
    }
    return false;
}

// When a flag-gated route is added after the page already loaded on its URL
// (e.g. a refresh directly on /marketing-dashboard), the initial navigation
// landed on the "*" pageNotFound route â€” re-navigate now that the route exists
// so it renders. Never test matched.length === 0: that is vue-router's START
// placeholder (fullPath "/") while the initial navigation is still resolving a
// lazy chunk, and replacing then sends the real URL to "/" â†’ /agenda (this
// redirected the embedded agenda webview away whenever the PostHog flags
// arrived before its chunk finished loading).
function reResolveCurrentRoute() {
    const current = router.currentRoute;
    if (current && current.name === "pageNotFound") {
        router.replace(current.fullPath).catch(() => { });
    }
}

// Check for PostHog readiness and add routes
function waitForPostHogAndAddRoutes() {
    // Try immediately first â€” call each independently to avoid && short-circuit
    const allAdded =
        [addKanbanRoutes(), addEmailWebsiteRoutes(), addDashboardRoutes(), addMarketingDashboardRoutes(), addAssistantArtifactRoutes(), addEnquiryCentreRoutes()]
            .every(Boolean);
    if (allAdded) {
        // Routes may have been added after the initial (unmatched) navigation on a
        // direct refresh â€” re-resolve so the flag-gated page renders instead of blank.
        reResolveCurrentRoute();
        return;
    }

    // If PostHog has onFeatureFlags method, use it
    if (typeof posthog.onFeatureFlags === 'function') {
        posthog.onFeatureFlags(() => {
            addKanbanRoutes();
            addEmailWebsiteRoutes();
            addDashboardRoutes();
            addMarketingDashboardRoutes();
            addAssistantArtifactRoutes();
            addEnquiryCentreRoutes();
            reResolveCurrentRoute();
        });
    } else {
        // Fallback: poll for feature flag availability
        let retryCount = 0;
        const maxRetries = 10;
        const retryInterval = 1000;

        const checkInterval = setInterval(() => {
            retryCount++;
            console.log(`Checking PostHog readiness - Attempt ${retryCount}`);

            const allDone =
                [addKanbanRoutes(), addEmailWebsiteRoutes(), addDashboardRoutes(), addMarketingDashboardRoutes(), addAssistantArtifactRoutes(), addEnquiryCentreRoutes()]
                    .every(Boolean);
            reResolveCurrentRoute();
            if (allDone) {
                clearInterval(checkInterval);
            } else if (retryCount >= maxRetries) {
                console.log('PostHog feature flags not available after maximum retries');
                clearInterval(checkInterval);
            }
        }, retryInterval);
    }
}

// Start checking for PostHog readiness
waitForPostHogAndAddRoutes();

// The flags can also arrive while the initial navigation is still pending (its
// current route is not pageNotFound yet) â€” re-check once it has settled
router.onReady(() => reResolveCurrentRoute());

resolveExpectedNavigationFailures(router);

// BeforeEach guard to check for open drawers and saving state
router.beforeEach((to, from, next) => {
    // The embedded agenda calendar is a single-page webview: nothing inside it
    // legitimately navigates the iframe to another app page. Block any escape and
    // report it â€” the guard runs synchronously inside the push, so the captured
    // stack names the caller.
    if (isAgendaWebviewLocation() && to.name !== ROUTER.AGENDA_WEBVIEW) {
        captureAppError(new Error(`Blocked navigation out of agenda-webview to ${to.fullPath}`), {
            tags: { origin: "agenda-webview.navigation-guard" },
            extra: { from: from.fullPath }
        });
        next(false);
        return;
    }

    // Prevent navigation while a save is in progress
    const isSaving = EDIT_STORE_MODULES.some(m => store.state[m]?.isSaving);
    if (isSaving) {
        Message.warning(i18n.t('SAVING_IN_PROGRESS').toString());
        next(false);
        return;
    }

    const drawers = store.state[commonModule].drawerModels;
    if (drawers.length > 0) {
        EventBus.$emit(EVENT_BUS.COMMON.WARNING_CLOSE_MODAL, to);
        return;
    }
    next();
});

export default router;